// Layer 3 (Transformation): polish.P9 (#657) — the metal's own path as the
// page's strip (locked comp `.impeccable/mocks/decision/metal-accounting.html`,
// variant A): Feed · Pb/Ag flotation · Zn flotation · Tails · Closure. Every
// figure is an existing engine read for the selected period (`periodView`
// through `MetalAccountingLayer.transform.ts`); this file only picks and
// formats them for the cells. Pure — no React.

import type { SamplePoint } from '@/versions/v1/schema/unit-operation';
import { GROSS_ERROR_FLAGS } from '@/versions/v1/shared/bunker-hill-accounting-seed';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { adherenceTone } from '@/versions/v1/modules/ingestion/ProcessGraph.transform';
import {
  closureGapPct,
  feedTonnageForPeriod,
  formatGapPct,
  formatMeasuredQuantity,
  isPeriodMeasured,
  lossDrillRead,
  massBalanceRows,
  metalAccountingLayer,
  periodWindowLabel,
  recoveryActualPct,
  recoveryDesignPct,
  tailsLossRead,
  threeProductRead,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';

export type BalanceStageId = 'feed' | 'pb' | 'zn' | 'tails' | 'closure';

export const BALANCE_STAGES: readonly BalanceStageId[] = ['feed', 'pb', 'zn', 'tails', 'closure'];

/** Strip cell → the graph node it selects. Closure is the whole plant's
 *  read, so it selects no single node. */
export const STAGE_NODE: Readonly<Record<BalanceStageId, string | null>> = {
  feed: 'sp-crush',
  pb: 'sp-pb',
  zn: 'sp-zn',
  tails: 'sp-paste',
  closure: null,
};

/** Graph node → the strip cell it belongs to. The process-water loop sits
 *  outside the metal's path and selects nothing. */
const NODE_STAGE: Readonly<Record<string, BalanceStageId>> = {
  'sp-crush': 'feed',
  'sp-grind': 'feed',
  'sp-pb': 'pb',
  'sp-zn': 'zn',
  'sp-filter': 'closure',
  'sp-paste': 'tails',
};

export function stageForNode(nodeId: string): BalanceStageId | null {
  return NODE_STAGE[nodeId] ?? null;
}

export function isBalanceStage(value: string | null | undefined): value is BalanceStageId {
  return value != null && (BALANCE_STAGES as readonly string[]).includes(value);
}

/** One run of a cell's comparison line; `strong` carries the lead term;
 *  `nowrap` keeps a figure with its unit or comparator on one line, so a
 *  narrow cell breaks the line only between reads. */
export type StripRun = { text: string; strong?: boolean; tone?: VizTone; nowrap?: boolean };

export type BalanceStripCell = {
  id: BalanceStageId;
  name: string;
  figure: string;
  unit: string | null;
  lines: readonly (readonly StripRun[])[];
  /** `lead` names the actor ahead of the label where the cell is wide enough (≥1400 px). */
  actor: { kind: 'System' | 'Engine'; label: string; lead?: string };
  pill: { label: string; tone: VizTone } | null;
  /** The closure cell ends the strip on the ok wash when the balance closes. */
  closes: boolean;
};

const DASH = '—';

/** Reads joined by ` · `, each kept whole: a narrow cell breaks the line only
 *  between reads, never inside `0.44 oz/t Ag` or `last 24 h`. */
function dotRuns(parts: readonly string[], base: Omit<StripRun, 'text'> = {}): StripRun[] {
  return parts.flatMap((part, index) => [
    ...(index > 0 ? [{ text: ' · ', ...base, nowrap: false }] : []),
    { text: part, ...base, nowrap: true },
  ]);
}

const UNMEASURED_LINE: readonly StripRun[] = [{ text: 'unmeasured' }];

function numberOnly(value: number, decimals?: number): string {
  return formatMeasuredQuantity({ value, unit: '' }, undefined, decimals === undefined ? undefined : { decimals });
}

function pct(value: number | null): string {
  return value === null ? DASH : `${value.toFixed(1)}%`;
}

/** One metal's head grade, back-calculated from the three-product balance
 *  (no head-assay tag exists): `5.44% Zn`, `0.44 oz/t Ag`. */
export function headGrade(element: 'Zn' | 'Pb' | 'Ag', view: PeriodView): string {
  const grade = threeProductRead(element, view).feedGrade;
  return grade.value === null ? `${DASH} ${element}` : formatMeasuredQuantity(grade, undefined, { decimals: 2 });
}

function headGrades(view: PeriodView): string[] {
  return (['Zn', 'Pb', 'Ag'] as const).map((element) => headGrade(element, view));
}

function concentrateLine(view: PeriodView, rowId: 'mb-pbcon' | 'mb-zncon'): StripRun[] {
  const row = massBalanceRows(view).find((entry) => entry.id === rowId);
  if (!row || row.tonnes === DASH) return [{ text: DASH }];
  if (rowId === 'mb-pbcon') {
    return dotRuns([`${row.tonnes} at ${Number(row.pbPct).toFixed(1)}% Pb`, `${row.agOzPerT} oz/t Ag`]);
  }
  return dotRuns([`${row.tonnes} at ${Number(row.znPct).toFixed(1)}% Zn`]);
}

function hasOpenCase(element: 'Zn' | 'Pb', view: PeriodView): boolean {
  return lossDrillRead(element, view).rows.some((row) => row.caseId !== null);
}

function sampleActor(id: string): BalanceStripCell['actor'] {
  return { kind: 'System', label: `${id} · sampled` };
}

function adherencePill(samplePoints: readonly SamplePoint[], id: string): BalanceStripCell['pill'] {
  const point = samplePoints.find((row) => row.id === id);
  if (!point || point.adherence === 'REG') return null;
  return { label: point.adherence.toLowerCase(), tone: adherenceTone(point.adherence) };
}

function thresholdWord(tone: string): string {
  return tone === 'bad' ? 'exceeds threshold' : tone === 'ok' ? 'within threshold' : 'no threshold';
}

function feedCell(view: PeriodView): BalanceStripCell {
  const feed = feedTonnageForPeriod(view);
  const anchor = GROSS_ERROR_FLAGS.some((flag) => flag.id === 'gef-wit021');
  return {
    id: 'feed',
    name: 'Feed · ROM ore',
    figure: feed.value === null ? DASH : numberOnly(feed.value),
    unit: feed.value === null ? null : feed.unit,
    lines:
      feed.value === null
        ? [UNMEASURED_LINE]
        : [
            dotRuns(headGrades(view), { strong: true }),
            dotRuns(['head back-calculated', windowText(view)]),
          ],
    actor: { kind: 'System', label: 'WIT-021 · metered' },
    pill: anchor ? { label: 'no cross-check', tone: 'warn' } : null,
    closes: false,
  };
}

function recoveryCell(view: PeriodView, element: 'Pb' | 'Zn'): BalanceStripCell {
  const actual = recoveryActualPct(element, view);
  const design = recoveryDesignPct(element);
  const pb = element === 'Pb';
  const lead: StripRun[] = [{ text: 'vs ' }, { text: `${pct(design)}`, strong: true }, { text: ' design' }];
  if (pb) {
    lead.push({ text: ' · ' }, { text: `Ag ${pct(recoveryActualPct('Ag', view))} vs ${pct(recoveryDesignPct('Ag'))}`, nowrap: true });
  }
  return {
    id: pb ? 'pb' : 'zn',
    name: pb ? 'Pb/Ag flotation' : 'Zn flotation',
    figure: pct(actual),
    unit: actual === null ? null : `${element} recovery`,
    lines: actual === null ? [UNMEASURED_LINE] : [lead, concentrateLine(view, pb ? 'mb-pbcon' : 'mb-zncon')],
    actor: sampleActor(pb ? 'SA03' : 'SA04'),
    pill: hasOpenCase(element, view) ? { label: 'open case', tone: 'bad' } : null,
    closes: false,
  };
}

/** The tails cell leads with the element whose tails assay breaks its
 *  threshold (Zn at the demo cursor); the loss fraction itself stays untoned
 *  — it has no threshold of its own (`tailsLossCell`, hard rule 1). */
function tailsCell(view: PeriodView, samplePoints: readonly SamplePoint[]): BalanceStripCell {
  const reads = (['Zn', 'Pb', 'Ag'] as const).map((element) => tailsLossRead(element, view));
  const lead = reads.find((read) => read.tailsAssayTone === 'bad') ?? reads[0]!;
  const rest = reads.filter((read) => read !== lead);
  const measured = lead.lossPct !== null && lead.tailsAssay !== null;
  const assay = (read: (typeof reads)[number]) =>
    read.tailsAssay ? formatMeasuredQuantity(read.tailsAssay, undefined, { decimals: 2 }) : DASH;
  // The secondary metals' assays drop the repeated element: `0.18%`, `0.08 oz/t`.
  const shortAssay = (read: (typeof reads)[number]) =>
    read.tailsAssay?.value == null ? DASH : `${read.tailsAssay.value.toFixed(2)}${read.element === 'Ag' ? ' oz/t' : '%'}`;
  return {
    id: 'tails',
    name: 'Tails · lost of feed metal',
    figure: measured ? pct(lead.lossPct) : DASH,
    unit: measured ? lead.element : null,
    lines: measured
      ? [
          [
            { text: `${assay(lead)} tails`, strong: true, nowrap: true, tone: lead.tailsAssayTone === 'bad' ? 'bad' : undefined },
            { text: ` ${thresholdWord(lead.tailsAssayTone)}` },
          ],
          rest.flatMap((read, index) => [
            ...(index > 0 ? [{ text: ' · ' }] : []),
            { text: `${read.element} ${pct(read.lossPct)} · ${shortAssay(read)}`, nowrap: true },
          ]),
        ]
      : [UNMEASURED_LINE],
    actor: sampleActor('SA05'),
    pill: adherencePill(samplePoints, 'SA05'),
    closes: false,
  };
}

/** Metal held in the circuit, back-calculated (the MA layer's in-circuit
 *  cell): `275 dst in circuit · back-calculated`, or its honest absence. The
 *  closure cell and the flowsheet panel's meta read the same line. */
export function inCircuitText(view: PeriodView): string {
  const cell = metalAccountingLayer(view).strip.cells.find((entry) => entry.id === 'ma-in-circuit');
  return cell && cell.value !== DASH ? `${cell.value} in circuit · back-calculated` : 'in circuit · unmeasured';
}

/** The period window in words (`last 24 h`, `week to date — 2–7 Jun`). */
export function windowText(view: PeriodView): string {
  return periodWindowLabel(view);
}

function closureCell(view: PeriodView): BalanceStripCell {
  const pbGap = closureGapPct('sp-pb', view);
  const znGap = closureGapPct('sp-zn', view);
  const check = massBalanceRows(view).find((row) => row.id === 'mb-closure')?.check ?? null;
  const measured = isPeriodMeasured(view) && pbGap !== null && znGap !== null;
  return {
    id: 'closure',
    name: 'Closure · two-path gap',
    figure: measured ? formatGapPct(pbGap!) : DASH,
    unit: measured ? 'Pb' : null,
    lines: measured
      ? [
          [
            { text: formatGapPct(znGap!), strong: true },
            { text: ' Zn · ' },
            ...(check ? [{ text: check.label.toLowerCase(), strong: true, tone: check.tone }] : []),
          ],
          dotRuns(inCircuitText(view).split(' · ')),
        ]
      : [UNMEASURED_LINE],
    actor: { kind: 'Engine', lead: 'Engine', label: 'three-product' },
    pill: null,
    closes: measured && check?.tone === 'ok',
  };
}

/** The five strip cells for the selected period. `samplePoints` are the
 *  phase-applied points the graph draws, so the SA05 pill and the graph's
 *  SA05 chip always agree. */
export function balanceStripCells(view: PeriodView, samplePoints: readonly SamplePoint[]): readonly BalanceStripCell[] {
  return [feedCell(view), recoveryCell(view, 'Pb'), recoveryCell(view, 'Zn'), tailsCell(view, samplePoints), closureCell(view)];
}

/** The default stage: the flotation circuit whose tails assay breaks its
 *  threshold (Zn at the demo cursor — it also carries the open air-valve
 *  case), else Zn. */
export function defaultBalanceStage(view: PeriodView): BalanceStageId {
  if (tailsLossRead('Zn', view).tailsAssayTone === 'bad') return 'zn';
  if (tailsLossRead('Pb', view).tailsAssayTone === 'bad') return 'pb';
  return 'zn';
}
